import React, { FC, useEffect } from 'react';

import AssignmentsList from 'src/components/assignments/assignments-list';
import { NoAssignmentDataView } from 'src/components/assignments/no-assignment-data-view';
import { LoadingScreen } from 'src/components/common/loaders';
// eslint-disable-next-line max-len
import { useMSWorkTypeAssignmentListData } from 'src/hooks/assignment-list-data/use-ms-work-type-assignment-list-data.hook';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import useEffectOnce from 'src/hooks/use-effect-once.hook';
import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll.hook';
import { AssignmentsWorkTypeMap } from 'src/interfaces/assignment.types';
import { XApiEventKeys } from 'src/interfaces/event.types';
import authService from 'src/services/api/auth.service';
import eventService from 'src/services/event.service';
import {
  setAssignmentsTotalCount,
  setDisplayedAssignmentsCount,
  setDoneAssignmentsCount,
  useActiveMSAssignmentsWorkType,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';

interface MiddleSchoolSectionProps {
  section: AssignmentsWorkTypeMap;
}

export const MiddleSchoolSection: FC<MiddleSchoolSectionProps> = ({
  section,
}) => {
  const isStudent = authService.isStudent();

  const activeMSAssignmentsWorkType = useActiveMSAssignmentsWorkType();
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();
  const dispatch = useAppDispatch();

  const {
    itemsToDisplay,
    allAssignmentCount,
    incrementItemsToDisplayCount,
    isLoading,
  } = useMSWorkTypeAssignmentListData(section);
  const { isLoadMoreRefVisible, setLoadMoreRef } = useInfiniteScroll();

  // Set assignments xAPI payload data.
  useEffect(() => {
    dispatch(setDisplayedAssignmentsCount(itemsToDisplay.length ?? 0));
    dispatch(setAssignmentsTotalCount(allAssignmentCount ?? 0));
    dispatch(setDoneAssignmentsCount(0));
  }, [allAssignmentCount, dispatch, itemsToDisplay.length]);

  useEffect(() => {
    if (isLoadMoreRefVisible) {
      incrementItemsToDisplayCount();
    }
  }, [isLoadMoreRefVisible, incrementItemsToDisplayCount]);

  useEffectOnce(() => {
    eventService.send(XApiEventKeys.viewed, undefined, {
      result: {
        isRightClick: false,
        ...commonXapiContextPayload,
      },
    });
  }, [itemsToDisplay]);

  if (activeMSAssignmentsWorkType !== section) return null;

  if (isLoading) {
    return isStudent ? <LoadingScreen isTransparentVersion={true} /> : null;
  }

  if (!itemsToDisplay.length) {
    return <NoAssignmentDataView isMiddleSchoolView={true} />;
  }

  return (
    <>
      <AssignmentsList assignments={itemsToDisplay} />
      <div ref={setLoadMoreRef} />
    </>
  );
};
